import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Users, 
  MapPin, 
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import widgetService from '../../services/widgetService';
import '../../styles/modern-design-system.css';

const LeadCaptureWidget = () => {
  console.log('🚀 LeadCaptureWidget component is mounting!');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    adults: 2,
    children: 0,
    destination: '',
    departureDate: '',
    returnDate: '',
    preferredAgent: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [agencyConfig, setAgencyConfig] = useState(null);
  const [agents, setAgents] = useState([]);

  // Get agency ID from URL path parameter
  const { agencyId } = useParams();
  
  // Debug logging
  // ============================================================================
  // SECURITY: Only log URL structure in dev, never full URLs (may contain tokens)
  // ============================================================================
  if (process.env.NODE_ENV !== 'production') {
    console.log('🔍 URL Debug Info:', {
      hasAgencyId: !!agencyId,
      pathname: window.location.pathname.replace(/[a-f0-9-]{36}/gi, '[UUID]')  // Mask UUIDs
    });
  }
  
  // Check if it's a valid agency ID or user ID
  const realAgencyId = agencyId && agencyId !== 'test' ? agencyId : null;

  useEffect(() => {
    console.log('Widget useEffect triggered with realAgencyId:', realAgencyId);
    
    if (!realAgencyId) {
      console.log('No realAgencyId, skipping API calls');
      return;
    }

    const fetchAgencyData = async () => {
      try {
        console.log('Starting to fetch agency data...');
        
        // Fetch agency configuration
        console.log('Fetching config for:', realAgencyId);
        const configResult = await widgetService.getAgencyConfig(realAgencyId);
        console.log('Config data received:', configResult);
        setAgencyConfig(configResult.data || {
          title: 'Request a Quote',
          primaryColor: '#3B82F6',
          showAgentSelection: true,
          agencyName: 'Your Travel Agency',
          agencyLogo: null
        });

        // Fetch agents list
        console.log('Fetching agents for:', realAgencyId);
        const agentsResult = await widgetService.getAgencyAgents(realAgencyId);
        console.log('Agents data received:', agentsResult);
        setAgents(agentsResult.data || []);
      } catch (error) {
        console.error('Error fetching agency data:', error);
        // Fallback to default configuration
        setAgencyConfig({
          title: 'Request a Quote',
          primaryColor: '#3B82F6',
          showAgentSelection: true,
          agencyName: 'Your Travel Agency',
          agencyLogo: null
        });
        setAgents([]);
      }
    };

    fetchAgencyData();
  }, [realAgencyId]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Use the widget service to submit the lead
      const result = await widgetService.submitLead({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        adults: formData.adults,
        children: formData.children,
        destination: formData.destination,
        departureDate: formData.departureDate,
        returnDate: formData.returnDate,
        preferredAgent: formData.preferredAgent,
        message: formData.message,
        agencyId: realAgencyId,
        source: 'widget'
      });

      if (result.success) {
        setSubmitted(true);
      } else {
        setError(result.error || 'Failed to submit form');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show error if no valid agency ID
  if (!realAgencyId) {
    return (
      <div className="flex justify-center items-center p-4 min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 w-full max-w-md text-center bg-white rounded-xl shadow-lg"
        >
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Invalid Widget</h2>
          <p className="mb-4 text-gray-600">
            This widget is not properly configured. Please contact the agency for assistance.
          </p>
          <p className="text-sm text-gray-500">
            Error: Invalid agency ID
          </p>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex justify-center items-center p-4 min-h-screen bg-gray-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-8 w-full max-w-md text-center bg-white rounded-xl shadow-lg"
        >
          <CheckCircle className="mx-auto mb-4 w-16 h-16 text-green-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Thank You!</h2>
          <p className="mb-4 text-gray-600">
            Your request has been submitted successfully. We'll get back to you within 24 hours.
          </p>
          <p className="text-sm text-gray-500">
            You can close this window now.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-4 min-h-screen bg-gray-50">
      
      <div className="mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden bg-white rounded-xl shadow-lg"
        >
          {/* Header */}
          <div className="px-6 py-8 text-white bg-gradient-to-r from-blue-600 to-blue-700">
            <h1 className="mb-2 text-2xl font-bold">
              {agencyConfig?.title || 'Request a Quote'}
            </h1>
            <p className="text-blue-100">
              Get your personalized Disney vacation quote in minutes
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Contact Information */}
            <div>
              <h3 className="flex items-center mb-4 text-lg font-semibold text-gray-900">
                <User className="mr-2 w-5 h-5" />
                Contact Information
              </h3>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your first name"
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    <Phone className="inline mr-1 w-4 h-4" />
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    <Mail className="inline mr-1 w-4 h-4" />
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
            </div>

            {/* Travel Party */}
            <div>
              <h3 className="flex items-center mb-4 text-lg font-semibold text-gray-900">
                <Users className="mr-2 w-5 h-5" />
                Travel Party
              </h3>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Number of Adults *
                  </label>
                  <input
                    type="number"
                    name="adults"
                    value={formData.adults}
                    onChange={handleInputChange}
                    min="1"
                    max="20"
                    required
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Number of Children
                  </label>
                  <input
                    type="number"
                    name="children"
                    value={formData.children}
                    onChange={handleInputChange}
                    min="0"
                    max="20"
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div>
              <h3 className="flex items-center mb-4 text-lg font-semibold text-gray-900">
                <MapPin className="mr-2 w-5 h-5" />
                Trip Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Destination *
                  </label>
                  <select
                    name="destination"
                    value={formData.destination}
                    onChange={handleInputChange}
                    required
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select your destination</option>
                    <option value="disney_world">Walt Disney World</option>
                    <option value="disneyland">Disneyland Resort</option>
                    <option value="disney_cruise">Disney Cruise Line</option>
                    <option value="universal">Universal Orlando</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <Calendar className="inline mr-1 w-4 h-4" />
                      Departure Date *
                    </label>
                    <input
                      type="date"
                      name="departureDate"
                      value={formData.departureDate}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      <Calendar className="inline mr-1 w-4 h-4" />
                      Return Date *
                    </label>
                    <input
                      type="date"
                      name="returnDate"
                      value={formData.returnDate}
                      onChange={handleInputChange}
                      required
                      className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Selection */}
            {agencyConfig?.showAgentSelection && agents.length > 0 && (
              <div>
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Preferred Agent (Optional)
                </h3>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Select a travel agent
                  </label>
                  <select
                    name="preferredAgent"
                    value={formData.preferredAgent}
                    onChange={handleInputChange}
                    className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No preference</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Leave blank if you have no preference
                  </p>
                </div>
              </div>
            )}

            {/* Additional Message */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Additional Information
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Tell us about your dream vacation, special occasions, or any specific requests..."
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center p-4 bg-red-50 rounded-lg border border-red-200">
                <AlertCircle className="mr-2 w-5 h-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex justify-center items-center px-6 py-3 w-full font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg transition-all duration-200 hover:from-blue-500 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="mr-2 w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 w-5 h-5" />
                    Get My Quote
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default LeadCaptureWidget;
